import { type RouteLocationNormalized, useRoute } from 'vue-router'
import { type DataLayerObject } from '@gtm-support/core'
import { useOnline } from '@vueuse/core'
import { useAppStore } from '~/base/stores/app'
import { useAppointmentStore } from '~/terminierungsportal/stores/appointment'

interface UserCentricsEvent {
  detail: { event: string }
}

let dataLayerEventQueue: DataLayerObject[] = []

export const useDataLayerMixin = () => {
  const appStore = useAppStore()
  const appointmentStore = useAppointmentStore()
  const route = useRoute()
  const isOnline = useOnline()

  const clickDLPush = (
    label: string,
    action = 'buttonclick',
    addToLayer = true
  ) => {
    const hostname = window.location.hostname
    if (addToLayer) {
      const event = {
        event: 'elementClick',
        eventInfo: {
          eventCategory: route.meta?.category,
          eventAction: action,
          eventLabel: label
        },
        page: {
          pageInfo: {
            hostname,
            documentURL: hostname + route.fullPath,
            pageName: route.meta.pageName,
            sysEnv: appStore.environment,
            breadcrumbs: route.name?.toString(),
            language: 'de-DE',
            referrer: hostname + route.fullPath,
            categoryForm: 'false'
          }
        },
        user: {
          userID: appointmentStore.appointmentId,
          status: isOnline.value ? 'online' : 'offline'
        }
      }
      pushDataLayerEvent(event)
    }
  }

  const eventDLPush = (
    label: string,
    eventAction: string,
    category?: string
  ) => {
    const hostname = window.location.hostname
    const event = {
      event: 'event',
      eventInfo: {
        eventCategory: category || route.meta?.category,
        eventAction,
        eventLabel: label
      },
      page: {
        pageInfo: {
          hostname,
          documentURL: hostname + route.fullPath,
          pageName: route.meta.pageName,
          sysEnv: appStore.environment,
          breadcrumbs: route.name?.toString(),
          language: 'de-DE',
          referrer: hostname + route.fullPath,
          categoryForm: 'false'
        }
      },
      user: {
        userID: appointmentStore.appointmentId,
        status: isOnline.value ? 'online' : 'offline'
      }
    }
    pushDataLayerEvent(event)
  }

  const formSubmitDLPush = (
    label: string,
    includeEmail: boolean,
    includeTel: boolean
  ) => {
    const hostname = window.location.hostname
    const event = {
      event: 'form_submit',
      eventInfo: {
        eventCategory: route.meta?.category,
        eventAction: 'formsubmit',
        eventLabel: label
      },
      page: {
        pageInfo: {
          hostname,
          documentURL: hostname + route.fullPath,
          pageName: route.meta.pageName,
          sysEnv: appStore.environment,
          breadcrumbs: route.name?.toString(),
          language: 'de-DE',
          referrer: hostname + route.fullPath,
          categoryForm: 'false'
        }
      },
      user: {
        userID: appointmentStore.appointmentId,
        status: isOnline.value ? 'online' : 'offline'
      },
      form: {
        include_email: includeEmail,
        include_tel: includeTel
      }
    }
    pushDataLayerEvent(event)
  }

  const eventPageLoad = (
    to: RouteLocationNormalized,
    environment: string,
    appointmentId: string
  ) => {
    const hostname = window.location.hostname
    const pageLoad = {
      event: 'page_load',
      eventInfo: {
        eventAction: 'pageload',
        eventCategory: to.meta?.category,
        eventLabel: to.meta?.label
      },
      page: {
        pageInfo: {
          hostname,
          documentURL: hostname + to.fullPath,
          pageName: to.meta?.pageName,
          sysEnv: environment,
          breadcrumbs: to.name?.toString(),
          language: 'de-DE',
          referrer: hostname + to.fullPath,
          categoryForm: 'true'
        }
      },
      user: {
        userID: appointmentId,
        status: isOnline.value ? 'online' : 'offline'
      }
    }
    if (isUserCentricsInitialized()) {
      pushDataLayerEvent(pageLoad)
    } else {
      handleFirstPageLoad(pageLoad)
    }
  }

  const pushDataLayerEvent = (event: DataLayerObject) => {
    if (isDataLayerReady()) {
      dataLayerEventQueue.push(event)
      return
    }
    window.dataLayer = window.dataLayer || []
    if (dataLayerEventQueue.length > 0) {
      window.dataLayer.push(...dataLayerEventQueue)
      dataLayerEventQueue = []
    }
    window.dataLayer.push(event)
  }

  const isDataLayerReady = () => {
    return !isOnline.value
  }

  const isUserCentricsInitialized = () => {
    return (
      window.dataLayer?.filter((e) => e.event === 'consent_status').length > 0
    )
  }

  const handleFirstPageLoad = (event: DataLayerObject) => {
    // If the user enters the page for the first time. It's okay to send multiple pageLoads
    // -> gtm will block each pageLoad until the user accepts the corresponding cookie settings
    window.addEventListener('ucEvent', (ucEvent: Event & UserCentricsEvent) => {
      if (ucEvent.detail && ucEvent.detail.event === 'consent_status') {
        pushDataLayerEvent(event)
      }
    })
  }

  return { clickDLPush, formSubmitDLPush, eventDLPush, eventPageLoad }
}
